import { useState, useContext, useEffect } from "react";
import useAxios from "../../hooks/useAxios";
import AuthContext from "../../contexts/auth-context";
import DoctorListPageStyles from './DoctorListPage.module';
import { motion } from 'framer-motion';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AppContext from '../../contexts/app-context';
import { PAGE_SIZE, urls, app_paths, patientFormType, departmentLabel, divisionLabel } from '../../components/config/variables';
import DoctorCard from "../../components/users/DoctorCard/DoctorCard";
import { pageTransition, pageVariants } from '../../constants/GlobalStyles';
import { NavLink } from 'react-router-dom';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FilterModal from "../../components/filter/FilterModal";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import TuneIcon from '@mui/icons-material/Tune';
import { Divider } from "@mui/material";

const DoctorListPage = () => {
    const classes = DoctorListPageStyles();
    const authCtx = useContext(AuthContext);
    const appCtx = useContext(AppContext);
    const { operation } = useAxios();

    const [searchQuery, setSearchQuery] = useState('');
    const [isSearch, setIsSearch] = useState(false);
    const [doctors, setDoctorsList] = useState([]);
    const [loading, setLoadingUsersList] = useState(false);
    const [error, setUsersListError] = useState();
    const [count, setCount] = useState(1);
    const [page, setPage] = useState(1);
    const [totalDoctors, setTotalDoctors] = useState(0);

    const [filterOpen, setFilterOpen] = useState(false);
    const [departmentFilter, setDepartmentFilter] = useState('');
    const [divisionFilter, setDivisionFilter] = useState('');

      // used to pass to the filters modal to update the filters after delete a spesific filter
      const [newFiltersAfterDelete, setNewFiltersAfterDelete] = useState(undefined)

    const setFilteredUserList = (doctorListResponse) => {
        setLoadingUsersList(false);
        setDoctorsList(doctorListResponse.results);
        setTotalDoctors(doctorListResponse.count)
        setCount(Math.ceil(doctorListResponse.count / PAGE_SIZE));
    };

    const getUsersList = (filter) => {
        setLoadingUsersList(true);
        const params = new URLSearchParams(filter);
        if (departmentFilter) {
            params.set('department', departmentFilter);
        }
        if (divisionFilter) {
            params.set('division', divisionFilter);
        }

        operation(
            {
                method: 'GET',
                url: urls.FACILITY_DOCTOR_LIST + '?' + params,
                headers: { Authorization: `token ${authCtx.token}` },
                data: {},
            },
            setFilteredUserList,
            handleUsersListError
        );
    };

    const handleUsersListError = (error) => {
        setLoadingUsersList(false);
        setUsersListError(error);
    };

    useEffect(() => {
        if (!appCtx.reFetchUsersList) {
            const searchForDoctor = `?q=${searchQuery}`;
            const filter = searchQuery
                ? `${searchForDoctor}&page=${page}&page_size=${PAGE_SIZE}`
                : `?page=${page}&page_size=${PAGE_SIZE}`;
            getUsersList(filter);
        }
        // eslint-disable-next-line
    }, [page, departmentFilter, divisionFilter]);

    const handleSearchClick = (event) => {
        if (searchQuery !== '') {
            event.preventDefault();
            setIsSearch(true);
            setPage(1);
            getUsersList(`?q=${searchQuery}&page=1&page_size=${PAGE_SIZE}`);
        }
    };

    const handleClearSearch = () => {
        setIsSearch(false);
        setSearchQuery('');
        setPage(1);
        getUsersList(`?page=1&page_size=${PAGE_SIZE}`);
    };

    useEffect(() => {
        if (isSearch && searchQuery === '') {
            handleClearSearch();
        }
        // eslint-disable-next-line
    }, [searchQuery, isSearch]);

    useEffect(() => {
        if (appCtx.reFetchUsersList) {
            if (appCtx.reFetchUsersList === patientFormType.create) {
                getUsersList(`?page=1&page_size=${PAGE_SIZE}`);
                setPage(1);
            } else {
                getUsersList(`?page=${page}&page_size=${PAGE_SIZE}`);
            }
        }
        // eslint-disable-next-line
    }, [appCtx.reFetchUsersList]);

    const handleApplyFilters = (selected) => {
        setDepartmentFilter(selected.department);
        setDivisionFilter(selected.division);
        setPage(1);
    };

    const deleteSpesificFilter = (filterKey) => {
    const updatedFilters = {
        department: departmentFilter,
        division: divisionFilter,
        [filterKey]: ''
    };

    handleApplyFilters(updatedFilters);
    setNewFiltersAfterDelete(updatedFilters);
};

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={pageTransition}
        >
            <Box>
                {/* top bar */}
                <Box
                    sx={classes.topBar}
                >
                    <Box sx={classes.topBarLeft}>
                        {/* Total Doctors */}
                        <Box
                            sx={classes.totalDoctorBox}
                        >
                            <Typography
                                sx={classes.totalDoctorLabel}
                            >
                                Total Doctors
                            </Typography>

                            {loading
                             ? <CircularProgress size={18} />
                             :<Typography
                                sx={classes.totaldoctorValue}
                            >
                                {totalDoctors}
                            </Typography>}
                        </Box>

                        <Divider orientation="vertical" flexItem />

                        {/* Search */}
                        <TextField
                            label="Search"
                            variant="outlined"
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(event) => event.key === "Enter" && handleSearchClick(event)}
                            InputProps={{
                                startAdornment: searchQuery && (
                                    <InputAdornment position="start">
                                        <IconButton size="small" onClick={handleClearSearch}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleSearchClick}>
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Filters Button */}
                        <Box
                            onClick={() => setFilterOpen(true)}
                            sx={classes.filtersButton}
                        >
                            <TuneIcon fontSize="small" />
                            <Typography fontWeight={600}>All Filters</Typography>
                        </Box>

                        {/* Active Filters */}
                        {[
                            { label: "Department", value: departmentFilter && departmentLabel[departmentFilter], key: "department" },
                            { label: "Division", value: divisionFilter && divisionLabel[divisionFilter], key: "division" },
                        ]
                            .filter(f => f.value)
                            .map(f => (
                                <Box
                                    key={f.key}
                                    sx={classes.filter}
                                >
                                    <Typography fontWeight={500}>
                                        {f.label}: {f.value}
                                    </Typography>

                                    <HighlightOffIcon
                                        onClick={() => deleteSpesificFilter(f.key)}
                                        sx={classes.deletefilterIcon}
                                    />
                                </Box>
                            ))}
                    </Box>

                    {/* Add Doctor Button */}
                    <Box sx={classes.topBarRight}>
                        <Divider orientation="vertical" flexItem />
                        <NavLink to={app_paths.NEW_DOCTOR} style={classes.navLink}>
                            <Box
                                sx={classes.newDoctorButton}
                            >
                                <AddCircleOutlineIcon />
                                <Typography fontWeight={600}>New Doctor</Typography>
                            </Box>
                        </NavLink>
                    </Box>
                </Box>

                {/* Filter Modal */}
                <FilterModal
                    open={filterOpen}
                    onClose={() => setFilterOpen(false)}
                    onApply={handleApplyFilters}
                    newFiltersAfterDelete={newFiltersAfterDelete}
                    initialFilters={{
                        department: departmentFilter,
                        division: divisionFilter,
                    }}
                    fields={["department", "division"]}
                />

                {/* Error State */}
                {error && (
                    <Alert severity="error" sx={classes.errorAlert}>
                        Failed to load doctors. Please try again.
                    </Alert>
                )}

                {/* Loading State */}
                {loading && (
                    <Box sx={classes.loadingContainer}>
                        <CircularProgress size={48} />
                    </Box>
                )}

                {/* Doctors Grid */}
                {!loading && doctors.length > 0 && (
                    <Box sx={classes.patientsGrid}>
                        {doctors.map((doctor) => (
                            <DoctorCard key={doctor.id} doctor={doctor} />
                        ))}
                    </Box>
                )}

                {/* Empty State */}
                {!loading && doctors.length === 0 && (
                    <Box sx={classes.emptyState}>
                        <PeopleOutlineIcon sx={classes.emptyStateIcon} />
                        <Typography sx={classes.emptyStateTitle}>
                            {searchQuery ? 'No doctors found' : 'No doctors yet'}
                        </Typography>
                        <Typography sx={classes.emptyStateText}>
                            {searchQuery
                                ? 'Try adjusting your search terms'
                                : 'Doctors will appear here once they are added to the system'}
                        </Typography>
                    </Box>
                )}

                {/* Pagination */}
                {!loading && doctors.length > 0 && count > 1 && (
                    <Box sx={classes.paginationContainer}>
                        <Pagination
                            count={count}
                            page={page}
                            onChange={(e, v) => setPage(v)}
                            color="primary"
                            size="large"
                        />
                    </Box>
                )}
            </Box>
        </motion.div>
    );
}

export default DoctorListPage;
